import { useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { FC } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Interface/Button/Button";
import { StepFlowNavigation } from "@/components/Layout/BaseLayouts/StepFlowLayout/StepFlowNavigation";
import { TwoColumnLayout } from "@/components/Layout/BaseLayouts/TwoColumnLayout";
import {
  getMovableSimIds,
  getSubscriptions,
} from "@/components/Portal/Documents/BillingAccounts/billingAccountUtils";
import { useAlert } from "@/hooks/useAlert";
import {
  getGetAccountListQueryKey,
  useTransferContractBillingAccount,
} from "@/services/api";
import { DrAccount } from "@/services/model";
import { AlertTypes, showsBillingActionBlockedModalAtom } from "@/utils/atoms";
import { isBillingActionBlockedError } from "@/utils/billingActionBlockedError";
import { log } from "@/utils/log";

import { BULK_MOVE_FORM_ID, BulkMoveFormValues } from "./bulkMoveFormValues";
import { SubscriptionSelection } from "./SubscriptionSelection";
import { TargetSelection } from "./TargetSelection";

interface Props {
  source: DrAccount;
  /** All Vertragskonten of the customer; the source is filtered out. */
  accounts: DrAccount[];
  /** Picked in the Vertragskonten list; only these Abos are listed here. */
  initialSimIds: number[];
  onDone: () => void;
}

export const BulkMoveForm: FC<Props> = ({
  source,
  accounts,
  initialSimIds,
  onDone,
}) => {
  const { t } = useTranslation("portal", {
    keyPrefix: "user-account.billingAccounts.bulkMove",
  });
  const queryClient = useQueryClient();
  const showAlert = useAlert();
  const showBlockedModal = useSetAtom(showsBillingActionBlockedModalAtom);
  const subscriptions = getSubscriptions(source).filter(
    ({ simId }) => simId != null && initialSimIds.includes(simId),
  );
  const targets = accounts.filter((account) => account.id !== source.id);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<BulkMoveFormValues>({
    defaultValues: {
      source,
      target: null,
      simIds: getMovableSimIds(subscriptions),
    },
  });
  const selectedSimIds = watch("simIds");
  const target = watch("target");

  const { mutate: transferSubscriptions, isLoading } =
    useTransferContractBillingAccount({
      mutation: {
        onSuccess: () => {
          showAlert({ type: AlertTypes.success, text: t("success") });
          queryClient.invalidateQueries(getGetAccountListQueryKey());
          onDone();
        },
        onError: (error) => {
          if (isBillingActionBlockedError(error)) {
            showBlockedModal(true);

            return;
          }

          showAlert({ type: AlertTypes.error, text: t("error") });
          log("Error moving subscriptions: " + error);
        },
      },
    });

  const onSubmit = (values: BulkMoveFormValues) => {
    if (values.target?.id == null || values.simIds.length === 0) {
      return;
    }

    transferSubscriptions({
      params: {
        simIds: values.simIds.map(Number),
        accountId: values.target.id,
      },
    });
  };

  return (
    <form
      id={BULK_MOVE_FORM_ID}
      data-testid={BULK_MOVE_FORM_ID}
      className="flex flex-col gap-6"
      noValidate
      onSubmit={handleSubmit(onSubmit)}
    >
      <TwoColumnLayout
        independentColumns
        rows={[
          {
            left: (
              <SubscriptionSelection
                source={source}
                subscriptions={subscriptions}
                selectedSimIds={selectedSimIds}
                control={control}
                register={register}
                errors={errors}
                onSelectionChange={(simIds) => setValue("simIds", simIds)}
              />
            ),
            right: (
              <TargetSelection
                targets={targets}
                selectedCount={selectedSimIds.length}
                control={control}
                disabled={isLoading}
              />
            ),
          },
        ]}
      />
      <StepFlowNavigation
        primaryButton={
          <Button
            dataTestid="bulk-move-submit"
            type="submit"
            form={BULK_MOVE_FORM_ID}
            variant="primary"
            disabled={!target || selectedSimIds.length === 0}
            loading={isLoading}
          >
            {t("submit")}
          </Button>
        }
      />
    </form>
  );
};
